import React, { useState, useEffect, useRef, useCallback } from "react";
import { createPortal } from "react-dom";
import { Paperclip, Download, Trash2, Loader2, Plus, X } from "lucide-react";
import { toast } from "sonner";
import { taskAttachmentsApi } from "../../lib/api";
import { AttachmentThumb } from "./AttachmentThumb";

const fmtSize = (b) => {
  if (b == null) return "";
  if (b < 1024) return `${b} B`;
  if (b < 1024 * 1024) return `${(b / 1024).toFixed(1)} KB`;
  return `${(b / 1024 / 1024).toFixed(1)} MB`;
};

// FAZ B — kopya dosya bilgilendirmesi. Dosya yine de göreve eklenir; bu yalnızca
// "zaten yüklü" bilgisidir + orijinalin konumuna gitme bağlantısı.
const jumpToTask = (taskId) => {
  try {
    window.__sertex_pending_task_jump = { task_id: taskId, ts: Date.now() };
  } catch { /* noop */ }
  window.dispatchEvent(
    new CustomEvent("sertex:task-jump", {
      detail: { task_id: taskId, clear_filters: true },
    }),
  );
};

const showDuplicateToast = (res) => {
  const loc = res.duplicate_location;
  if (!loc || res.duplicate_access !== "ok") {
    toast("Bu dosya zaten yüklü, tekrar yer kaplamadı", {
      duration: 6000,
      description: "Orijinalin konumu gösterilemiyor.",
    });
    return;
  }
  const extra = res.duplicate_count > 1 ? ` · ${res.duplicate_count} görevde mevcut` : "";
  toast(
    <span data-testid="dup-file-toast">
      Bu dosya zaten yüklü{" "}
      <button
        type="button"
        data-testid="dup-file-goto-btn"
        onClick={() => { toast.dismiss(); jumpToTask(loc.task_id); }}
        className="underline text-sertex-cyan hover:text-white transition-colors"
      >
        (konumu görmek için tıklayın)
      </button>
      <span className="block text-[11px] opacity-70 mt-0.5">
        {loc.task_title}{extra}
      </span>
    </span>,
    { duration: 10000 },
  );
};

// Hata sebebini SADE TÜRKÇE + kod ile anlatır (kullanıcı okuyup iletebilsin).
const hataMesaji = (e) => {
  const st = e?.response?.status;
  const detail = String(e?.response?.data?.detail || "");
  if (!st) return "Sunucuya ulaşılamadı (kod ağ)";
  if (st === 404) {
    if (detail.includes("404-ek") || detail.toLowerCase().includes("ek kaydı")) {
      return "Ek kaydı veritabanında yok (kod 404-ek)";
    }
    return "Görev bulunamadı ya da erişim izni yok (kod 404-görev)";
  }
  if (st === 403) return "Bu dosyayı görme izniniz yok (kod 403)";
  if (st === 502 || detail.includes("502-depo")) {
    return "Dosya depolama alanında bulunamadı, yükleme tamamlanmamış olabilir (kod 502-depo)";
  }
  return `Beklenmeyen hata (kod ${st})`;
};

// Görev kartına / Düzenle penceresine gömülen "📎 Dosyalar" bölümü.
// Yükle (parçalı) + listele + tıkla-indir + sil. Kendi verisini yükler.
export const TaskAttachments = ({ taskId, currentUserId, canManage = false, compact = false, attachmentApi = taskAttachmentsApi }) => {
  const [items, setItems] = useState(null); // null=loading
  const [uploading, setUploading] = useState(null); // {name, pct}
  const [downloadingId, setDownloadingId] = useState(null);
  const [preview, setPreview] = useState(null); // {att, url, kind: "image"|"pdf"}
  const [tani, setTani] = useState(null); // {att, veri} — NEDEN? sonucu
  const [onariliyor, setOnariliyor] = useState(false);
  const onarimKilidi = useRef(false);
  const yuklemeKilidi = useRef(false);
  const inputRef = useRef(null);

  const load = useCallback(() => {
    attachmentApi.list(taskId)
      .then((r) => setItems(r || []))
      .catch(() => setItems([]));
  }, [taskId]);

  useEffect(() => { load(); }, [load]);

  // Yükleme sürerken sekme kapatma/yenileme → tarayıcı uyarısı.
  useEffect(() => {
    if (!uploading) return;
    const onUnload = (e) => {
      e.preventDefault();
      e.returnValue = "Dosya yükleniyor, şimdi çıkarsanız yükleme tamamlanmaz";
      return e.returnValue;
    };
    window.addEventListener("beforeunload", onUnload);
    return () => window.removeEventListener("beforeunload", onUnload);
  }, [uploading]);

  const handleFiles = async (fileList) => {
    if (yuklemeKilidi.current) return; // senkron çift-gönderim kilidi
    yuklemeKilidi.current = true;
    try {
      await _handleFiles(fileList);
    } finally {
      yuklemeKilidi.current = false;
    }
  };

  const _handleFiles = async (fileList) => {
    const files = Array.from(fileList || []);
    if (!files.length) return;
    for (const f of files) {
      if (f.size > 100 * 1024 * 1024) {
        toast.error(`${f.name}: dosya çok büyük (maks 100 MB)`);
        continue;
      }
      setUploading({ name: f.name, pct: 0 });
      try {
        const res = await attachmentApi.upload(taskId, f, (pct) =>
          setUploading({ name: f.name, pct }),
        );
        if (res?.duplicate) {
          showDuplicateToast(res);
        } else {
          toast.success(`Dosya eklendi: ${f.name}`);
        }
      } catch (e) {
        const st = e?.response?.status || "ağ";
        toast.error(
          `Yükleme tamamlanmadı, dosya kaydedilmedi — tekrar deneyin (kod ${st})`,
          { duration: 12000 },
        );
      }
    }
    setUploading(null);
    if (inputRef.current) inputRef.current.value = "";
    load();
  };

  const handleDownload = async (att) => {
    setDownloadingId(att.id);
    try {
      const res = await attachmentApi.download(taskId, att.id);
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = att.original_filename || "dosya";
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      hataGoster(e, att);
    } finally {
      setDownloadingId(null);
    }
  };

  // Hata bildirimi + "NEDEN?" düğmesi (tanılama ucunu çağırır).
  const hataGoster = (e, att) => {
    toast.error(hataMesaji(e), {
      duration: 12000,
      action: { label: "NEDEN?", onClick: () => neden(att) },
    });
  };

  const neden = async (att) => {
    try {
      const veri = await attachmentApi.diagnose(taskId, att.id);
      setTani({ att, veri });
    } catch (e) {
      toast.error(`Tanılama yapılamadı (kod ${e?.response?.status || "ağ"})`);
    }
  };

  // ONARMAYI DENE — depoda dosyanın gerçek adresini arar, hiçbir şey silmez.
  const onarmayiDene = async (att) => {
    if (onarimKilidi.current) return;
    onarimKilidi.current = true;
    setOnariliyor(true);
    try {
      const r = await attachmentApi.repair(taskId, att.id);
      if (r.onarildi) {
        toast.success("Dosya bulundu ve bağlantı onarıldı — tekrar deneyin", { duration: 10000 });
        setTani(null);
        load();
      } else {
        toast.error(
          `Depoda bu dosyaya ait hiçbir kopya bulunamadı (${r.denenen_yol_sayisi} farklı adres denendi${
            r.aday_sayisi ? `, ${r.aday_sayisi} belirsiz aday` : ""
          })`,
          { duration: 12000 },
        );
        const veri = await attachmentApi.diagnose(taskId, att.id);
        setTani({ att, veri });
      }
    } catch (e) {
      toast.error(`Onarım denenemedi (kod ${e?.response?.status || "ağ"})`);
    } finally {
      setOnariliyor(false);
      onarimKilidi.current = false;
    }
  };

  const previewKind = (att) => {
    const ct = (att.content_type || "").toLowerCase();
    const name = (att.original_filename || "").toLowerCase();
    if (ct.startsWith("image/") || /\.(png|jpe?g|gif|webp|bmp|svg)$/.test(name)) return "image";
    if (ct.includes("pdf") || name.endsWith(".pdf")) return "pdf";
    return null;
  };

  const openPreview = async (att) => {
    const kind = previewKind(att);
    if (!kind) return handleDownload(att); // önizlenemeyen tür → indir
    setDownloadingId(att.id);
    try {
      const res = await attachmentApi.download(taskId, att.id);
      const url = URL.createObjectURL(res.data);
      setPreview({ att, url, kind });
    } catch (e) {
      hataGoster(e, att);
    } finally {
      setDownloadingId(null);
    }
  };

  const closePreview = () => {
    setPreview((p) => {
      if (p) URL.revokeObjectURL(p.url);
      return null;
    });
  };

  const handleDelete = async (att) => {
    try {
      await attachmentApi.remove(taskId, att.id);
      setItems((prev) => (prev || []).filter((x) => x.id !== att.id));
      toast.success("Dosya silindi");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Silinemedi");
    }
  };

  const count = items ? items.length : 0;

  return (
    <div
      className="mt-2 border-t border-sertex-cyan/15 pt-1.5"
      data-testid={`task-attachments-${taskId}`}
      onClick={(e) => e.stopPropagation()}
    >
      <div className="flex items-center justify-between mb-1">
        <div className="hud-text text-sertex-textMuted flex items-center gap-1">
          <Paperclip className="h-3 w-3" /> DOSYALAR{count > 0 ? ` (${count})` : ""}
        </div>
        <button
          onClick={(e) => { e.stopPropagation(); inputRef.current?.click(); }}
          disabled={!!uploading}
          data-testid={`attach-upload-btn-${taskId}`}
          className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded border border-sertex-cyan/30 text-sertex-cyan hover:bg-sertex-cyan/10 hud-text transition-colors disabled:opacity-40"
        >
          <Plus className="h-3 w-3" /> DOSYA EKLE
        </button>
        <input
          ref={inputRef}
          type="file"
          multiple
          onChange={(e) => handleFiles(e.target.files)}
          data-testid={`attach-upload-input-${taskId}`}
          className="hidden"
        />
      </div>

      {uploading && (
        <div
          className="flex items-center gap-2 px-2 py-1 rounded bg-sertex-cyan/5 border border-sertex-cyan/20 mb-1"
          data-testid={`attach-uploading-${taskId}`}
        >
          <Loader2 className="h-3 w-3 text-sertex-cyan animate-spin shrink-0" />
          <div className="flex-1 min-w-0">
            <div className="text-[11px] font-mono text-sertex-text truncate">{uploading.name}</div>
            <div className="h-1 bg-sertex-surface rounded overflow-hidden mt-0.5">
              <div
                className="h-full bg-sertex-cyan transition-all"
                style={{ width: `${uploading.pct}%` }}
              />
            </div>
          </div>
          <span className="hud-text text-sertex-cyan">%{uploading.pct}</span>
        </div>
      )}

      {items === null ? (
        <div className="hud-text text-sertex-textMuted py-1">Yükleniyor…</div>
      ) : items.length === 0 ? (
        !uploading && (
          <div
            className="hud-text text-sertex-textMuted/70 py-1"
            data-testid={`attach-empty-${taskId}`}
          >
            Henüz dosya yok — inceleme için dosya ekleyin.
          </div>
        )
      ) : (
        <div className="space-y-1">
          {items.map((att) => {
            const canDelete = canManage || att.uploaded_by === currentUserId;
            return (
              <div
                key={att.id}
                data-testid={`attach-item-${att.id}`}
                className="flex items-center gap-2 px-2 py-1 rounded border border-sertex-cyan/15 hover:border-sertex-cyan/40 bg-sertex-surface/40 transition-colors group"
              >
                <AttachmentThumb att={att} taskId={taskId} api={attachmentApi} />
                <button
                  onClick={() => openPreview(att)}
                  data-testid={`attach-open-${att.id}`}
                  title="Önizle"
                  className="flex-1 min-w-0 text-left"
                >
                  <div className="text-[12px] font-mono text-sertex-text truncate group-hover:text-sertex-cyan transition-colors">
                    {att.original_filename}
                  </div>
                  {!compact && (
                    <div className="hud-text text-sertex-textMuted/60 truncate">
                      {fmtSize(att.size)}
                      {att.uploaded_by_name ? ` · ${att.uploaded_by_name}` : ""}
                    </div>
                  )}
                </button>
                {downloadingId === att.id ? (
                  <Loader2 className="h-3.5 w-3.5 text-sertex-cyan animate-spin shrink-0" />
                ) : (
                  <button
                    onClick={() => handleDownload(att)}
                    data-testid={`attach-download-icon-${att.id}`}
                    title="İndir"
                    className="p-1 text-sertex-textMuted hover:text-sertex-cyan transition-colors shrink-0"
                  >
                    <Download className="h-3.5 w-3.5" />
                  </button>
                )}
                {canDelete && (
                  <button
                    onClick={() => handleDelete(att)}
                    data-testid={`attach-delete-${att.id}`}
                    title="Sil"
                    className="p-1 text-sertex-textMuted hover:text-rose-400 transition-colors shrink-0"
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                  </button>
                )}
              </div>
            );
          })}
        </div>
      )}

      {preview &&
        createPortal(
          <div
            className="fixed inset-0 z-[9999] bg-black/90 flex flex-col"
            data-testid="attach-preview-modal"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center gap-2 px-4 py-3 border-b border-white/10">
              <span className="flex-1 min-w-0 truncate text-sm font-mono text-white">
                {preview.att.original_filename}
              </span>
              <button
                onClick={() => handleDownload(preview.att)}
                title="İndir"
                data-testid="attach-preview-download"
                className="p-2 text-white/80 hover:text-sertex-cyan transition-colors"
              >
                <Download className="h-5 w-5" />
              </button>
              <button
                onClick={closePreview}
                title="Kapat"
                data-testid="attach-preview-close"
                className="p-2 text-white/80 hover:text-white transition-colors"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            <div className="flex-1 min-h-0 flex items-center justify-center p-2">
              {preview.kind === "image" ? (
                <img
                  src={preview.url}
                  alt={preview.att.original_filename}
                  data-testid="attach-preview-image"
                  className="max-h-full max-w-full object-contain"
                />
              ) : (
                <iframe
                  src={preview.url}
                  title={preview.att.original_filename}
                  data-testid="attach-preview-frame"
                  className="w-full h-full bg-white rounded"
                />
              )}
            </div>
          </div>,
          document.body,
        )}
      {tani &&
        createPortal(
          <div
            className="fixed inset-0 z-[10000] bg-black/85 flex items-center justify-center p-4"
            data-testid="attach-diagnose-modal"
            onClick={() => setTani(null)}
          >
            <div
              className="w-full max-w-md rounded-lg border border-sertex-cyan/40 bg-sertex-bg p-4 space-y-2"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="hud-text text-sertex-cyan">DOSYA NEDEN AÇILMIYOR?</div>
              <div className="text-[12px] font-mono text-sertex-text truncate">
                {tani.att.original_filename}
              </div>
              <ul className="text-[12px] font-mono space-y-1" data-testid="attach-diagnose-list">
                {[
                  ["Görev bulundu", tani.veri.task_found],
                  ["Erişim izni var", tani.veri.permission_ok],
                  ["Ek kaydı bulundu", tani.veri.record_found],
                  ["Dosya yolu kayıtlı", tani.veri.storage_path_var_mi],
                  ["Dosya depoda var", tani.veri.storage_object_exists],
                ].map(([ad, ok]) => (
                  <li key={ad} className={ok ? "text-emerald-300" : "text-rose-300"}>
                    {ad} {ok ? "✓" : ok === null ? "— (kontrol edilemedi)" : "✗"}
                  </li>
                ))}
              </ul>
              <div className="hud-text text-sertex-textMuted">
                Boyut: {fmtSize(tani.veri.size)} · Tür: {tani.veri.content_type || "—"} · Yükleyen:{" "}
                {tani.veri.uploaded_by || "—"}
              </div>
              {tani.veri.storage_path && (
                <div
                  className="text-[10px] font-mono text-sertex-textMuted break-all"
                  data-testid="attach-diagnose-path"
                >
                  Kayıtlı adres: {tani.veri.storage_path}
                </div>
              )}
              {tani.veri.storage_object_exists === false && (
                <button
                  onClick={() => onarmayiDene(tani.att)}
                  disabled={onariliyor}
                  data-testid="attach-repair-btn"
                  className="w-full py-2 border border-amber-400/50 text-amber-200 hover:bg-amber-500/15 rounded hud-text transition-colors disabled:opacity-50"
                >
                  {onariliyor ? "ARANIYOR…" : "ONARMAYI DENE"}
                </button>
              )}
              <button
                onClick={() => setTani(null)}
                data-testid="attach-diagnose-close"
                className="w-full py-2 border border-sertex-cyan/40 text-sertex-cyan hover:bg-sertex-cyan/10 rounded hud-text transition-colors"
              >
                KAPAT
              </button>
            </div>
          </div>,
          document.body,
        )}
    </div>
  );
};

export default TaskAttachments;
